import { useState, useEffect } from 'react';
import api from '../lib/api';

export type CatalogRow = { id: string; en: string; ar: string };

export function slugify(label: string): string {
  const t = (label || '').trim();
  if (!t) return 'item';
  const ascii = t
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (ascii.length > 0) return ascii.slice(0, 64);
  let h = 0;
  for (let i = 0; i < t.length; i++) {
    h = (h * 31 + t.charCodeAt(i)) | 0;
  }
  return `c${Math.abs(h).toString(16)}`.slice(0, 64);
}

function normalizeLoaded(value: unknown): CatalogRow[] {
  if (!Array.isArray(value)) return [];
  const rows: CatalogRow[] = [];
  for (const el of value) {
    if (typeof el === 'string') {
      const en = el.trim();
      if (!en) continue;
      rows.push({ id: slugify(en), en, ar: en });
      continue;
    }
    if (el && typeof el === 'object') {
      const o = el as Record<string, unknown>;
      const en = String(o.en ?? o.nameEn ?? o.label ?? '').trim();
      const ar = String(o.ar ?? o.nameAr ?? '').trim();
      const enFinal = en || ar;
      const arFinal = ar || en;
      if (!enFinal) continue;
      let id = String(o.id ?? '').trim();
      if (!id) id = slugify(enFinal);
      rows.push({ id: id.slice(0, 64), en: enFinal, ar: arFinal });
    }
  }
  return rows;
}

const defaultRows: CatalogRow[] = [
  { id: 'egyptian', en: 'Egyptian', ar: 'Egyptian' },
  { id: 'italian', en: 'Italian', ar: 'Italian' },
  { id: 'asian', en: 'Asian', ar: 'Asian' },
  { id: 'healthy', en: 'Healthy', ar: 'Healthy' },
  { id: 'seafood', en: 'Seafood', ar: 'Seafood' },
];

export default function CuisineCatalogSettings() {
  const [rows, setRows] = useState<CatalogRow[]>([...defaultRows]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/settings');
        const data = res.data?.data ?? res.data ?? {};
        const catalog = data.chefCuisineCatalog;
        const pr = data.chefOnDemandPricing;
        if (Array.isArray(catalog) && catalog.length > 0) {
          setRows(normalizeLoaded(catalog));
        } else if (Array.isArray(pr?.cuisineOptions) && pr.cuisineOptions.length > 0) {
          setRows(normalizeLoaded(pr.cuisineOptions));
        }
      } catch {
        setMessage('Failed to load cuisines');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setMessage('');
    try {
      const cleaned = rows
        .map((r) => ({
          id: (r.id || slugify(r.en)).trim().slice(0, 64),
          en: r.en.trim(),
          ar: (r.ar || r.en).trim(),
        }))
        .filter((r) => r.en.length > 0);
      const seen = new Set<string>();
      const unique = cleaned.filter((r) => {
        if (seen.has(r.id)) return false;
        seen.add(r.id);
        return true;
      });
      await api.put('/admin/settings/chefCuisineCatalog', { value: unique });
      setRows(unique);
      setMessage('Cuisines saved');
    } catch {
      setMessage('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => {
    setRows((prev) => [...prev, { id: '', en: '', ar: '' }]);
  };

  if (loading) return <div className="p-6">Loading…</div>;

  return (
    <div className="p-6 max-w-5xl space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Cuisines</h1>
      <p className="text-sm text-gray-600 max-w-2xl">
        Bilingual catalog (English + Arabic). The mobile app shows the correct language based on user settings. Stable{' '}
        <code className="bg-gray-100 px-1 rounded">id</code> is stored on chef profiles for filtering — edit with care or
        run the DB migration after changing ids.
      </p>
      {message && <p className="text-sm text-gray-600">{message}</p>}

      <section>
        <h2 className="text-lg font-semibold mb-2">Catalog</h2>
        <div className="overflow-x-auto border rounded-lg bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b bg-gray-50 text-left">
                <th className="p-3 font-semibold text-gray-700">Id (slug)</th>
                <th className="p-3 font-semibold text-gray-700">English</th>
                <th className="p-3 font-semibold text-gray-700">Arabic</th>
                <th className="p-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="p-2 align-top">
                    <input
                      className="w-full border rounded px-2 py-1.5 font-mono text-xs"
                      value={r.id}
                      placeholder={slugify(r.en)}
                      onChange={(e) => {
                        const id = e.target.value;
                        setRows((prev) => prev.map((x, j) => (j === i ? { ...x, id } : x)));
                      }}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <input
                      className="w-full border rounded px-2 py-1.5"
                      value={r.en}
                      onChange={(e) => {
                        const en = e.target.value;
                        setRows((prev) =>
                          prev.map((x, j) => (j === i ? { ...x, en, id: x.id || slugify(en) } : x)),
                        );
                      }}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <input
                      className="w-full border rounded px-2 py-1.5"
                      dir="rtl"
                      value={r.ar}
                      onChange={(e) => {
                        const ar = e.target.value;
                        setRows((prev) => prev.map((x, j) => (j === i ? { ...x, ar } : x)));
                      }}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <button
                      type="button"
                      className="text-red-600 text-sm"
                      onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className="px-3 py-2 bg-gray-100 rounded-lg text-sm font-medium" onClick={addRow}>
            Add row
          </button>
          <button
            type="button"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg disabled:opacity-50"
            disabled={saving}
            onClick={save}
          >
            Save cuisines
          </button>
        </div>
      </section>
    </div>
  );
}
